import { useState, useCallback, useEffect, useSyncExternalStore } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { useQueryClient } from '@tanstack/react-query'
import { useOrganizacion } from '@/core/providers/OrganizacionProvider'
import { toast } from 'sonner'
import { afipDelegacionService, type AbrirAfipDirectoResult } from '@/services/afipDelegacionService'
import {
  bloquearSiEsDeOtro,
  bloquearVault,
  desbloquearSiNoCambio,
  generacionVault,
  pinVigente,
  suscribirVault,
  tiempoRestanteVault,
} from './vaultSession'

interface ClaveCliente {
  id: string
  organismo: string
  usuario: string | null
  clave: string | null
  notas: string | null
  created_at: string
}

/** Clave fiscal de una tarea de nueva inscripción, descifrada. */
interface ClaveInscripcion {
  id: string
  organismo: string
  usuario: string | null
  clave: string
  created_at: string
}

interface UseVaultReturn {
  // PIN state
  hasPin: boolean | null
  pinLoading: boolean
  // Session state
  desbloqueado: boolean
  tiempoRestante: number
  // Actions
  checkPinStatus: () => Promise<void>
  verificarPin: (pin: string) => Promise<boolean>
  configurarPin: (pin: string) => Promise<boolean>
  cambiarPin: (oldPin: string, newPin: string) => Promise<boolean>
  bloquear: () => void
  // Claves CRUD
  obtenerClaves: (clienteId: string) => Promise<ClaveCliente[]>
  crearClave: (clienteId: string, data: { organismo: string; usuario?: string; clave: string; notas?: string; organizacionId?: string }) => Promise<string>
  actualizarClave: (id: string, data: { organismo?: string; usuario?: string; clave?: string; notas?: string }) => Promise<void>
  eliminarClave: (id: string) => Promise<void>
  // AFIP acceso directo
  abrirAfipDirecto: (clienteId: string) => Promise<AbrirAfipDirectoResult>
  // Clave fiscal de una tarea de inscripción
  obtenerClavesInscripcion: (tareaId: string) => Promise<ClaveInscripcion[]>
  guardarClaveInscripcion: (tareaId: string, data: { organismo: string; usuario: string; clave: string }) => Promise<string>
  organismosConClaveInscripcion: (tareaId: string) => Promise<string[]>
  transferirClavesInscripcion: (tareaId: string, clienteId: string) => Promise<number>
}

/** Query de los organismos con clave fiscal de una tarea de inscripción. */
export const clavesInscripcionKey = (tareaId: string) => ['claves-inscripcion', tareaId] as const

const vaultFetch = async (
  endpoint: string,
  token: string,
  body: Record<string, unknown>,
  extraHeaders?: Record<string, string>
) => {
  const res = await fetch(`/.netlify/functions/${endpoint}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...extraHeaders,
    },
    body: JSON.stringify(body),
  })

  const data = await res.json()

  if (!res.ok) {
    if (res.status === 429) bloquearVault()
    throw new Error(data.error || data.message || 'Error en vault')
  }

  return data
}

export function useVault(): UseVaultReturn {
  const { getAccessTokenSilently, user } = useAuth0()
  const { membresia } = useOrganizacion()
  const queryClient = useQueryClient()
  const orgId = membresia?.organizacion_id ?? ''
  const userSub = user?.sub ?? ''
  // Which clients have an ARCA key (`useClientesConClaveArca`) changes with every write.
  const invalidarClaves = useCallback(
    () => queryClient.invalidateQueries({ queryKey: ['claves-cliente'] }),
    [queryClient],
  )
  const [hasPin, setHasPin] = useState<boolean | null>(null)
  const [pinLoading, setPinLoading] = useState(false)
  const tiempoRestante = useSyncExternalStore(suscribirVault, () => tiempoRestanteVault(orgId, userSub))
  const desbloqueado = tiempoRestante > 0

  // Al cambiar de usuario o de organización, el PIN de la sesión anterior se borra de memoria.
  useEffect(() => {
    if (orgId && userSub) bloquearSiEsDeOtro(orgId, userSub)
  }, [orgId, userSub])

  const pinHeaders = useCallback((): Record<string, string> | undefined => {
    const pin = pinVigente(orgId, userSub)
    return pin ? { 'X-Vault-Pin': pin } : undefined
  }, [orgId, userSub])

  const bloquear = bloquearVault

  const checkPinStatus = useCallback(async () => {
    setPinLoading(true)
    try {
      const token = await getAccessTokenSilently()
      const data = await vaultFetch('vault-pin', token, { action: 'status' })
      setHasPin(data.hasPin)
    } catch (err) {
      console.error('Error checking PIN status:', err)
    } finally {
      setPinLoading(false)
    }
  }, [getAccessTokenSilently])

  const verificarPin = useCallback(async (pin: string): Promise<boolean> => {
    const generacion = generacionVault()
    try {
      const token = await getAccessTokenSilently()
      const data = await vaultFetch('vault-pin', token, { action: 'verify', pin })

      if (data.valid) {
        desbloquearSiNoCambio(generacion, pin, orgId, userSub)
        return true
      }
      return false
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error al verificar PIN')
      return false
    }
  }, [getAccessTokenSilently, orgId, userSub])

  const configurarPin = useCallback(async (pin: string): Promise<boolean> => {
    const generacion = generacionVault()
    try {
      const token = await getAccessTokenSilently()
      await vaultFetch('vault-pin', token, { action: 'setup', pin })
      setHasPin(true)
      // ORR-206: retener el PIN recién creado en la sesión (igual que verificarPin).
      // Sin esto, obtenerClaves/abrirAfipDirecto se disparan sin header X-Vault-Pin y
      // fallan ("Error al cargar claves" / "Vault bloqueado") pese al setup exitoso.
      desbloquearSiNoCambio(generacion, pin, orgId, userSub)
      toast.success('PIN configurado correctamente')
      return true
    } catch {
      toast.error('Error al configurar PIN')
      return false
    }
  }, [getAccessTokenSilently, orgId, userSub])

  const cambiarPin = useCallback(async (oldPin: string, newPin: string): Promise<boolean> => {
    try {
      const token = await getAccessTokenSilently()
      await vaultFetch('vault-pin', token, { action: 'change', oldPin, newPin })
      toast.success('PIN cambiado correctamente')
      return true
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error al cambiar PIN')
      return false
    }
  }, [getAccessTokenSilently])

  const obtenerClaves = useCallback(async (clienteId: string): Promise<ClaveCliente[]> => {
    const token = await getAccessTokenSilently()
    const data = await vaultFetch('vault-claves', token, { action: 'list', clienteId, organizacionId: orgId }, pinHeaders())
    return data.claves ?? []
  }, [getAccessTokenSilently, orgId, pinHeaders])

  const crearClave = useCallback(async (
    clienteId: string,
    input: { organismo: string; usuario?: string; clave: string; notas?: string; organizacionId?: string }
  ): Promise<string> => {
    const token = await getAccessTokenSilently()
    const data = await vaultFetch('vault-claves', token, {
      action: 'create',
      clienteId,
      organizacionId: input.organizacionId || orgId,
      ...input,
    }, pinHeaders())
    toast.success('Clave guardada')
    void invalidarClaves()
    return data.id
  }, [getAccessTokenSilently, orgId, pinHeaders, invalidarClaves])

  const actualizarClave = useCallback(async (
    id: string,
    input: { organismo?: string; usuario?: string; clave?: string; notas?: string }
  ): Promise<void> => {
    const token = await getAccessTokenSilently()
    await vaultFetch('vault-claves', token, { action: 'update', id, organizacionId: orgId, ...input }, pinHeaders())
    toast.success('Clave actualizada')
    void invalidarClaves()
  }, [getAccessTokenSilently, orgId, pinHeaders, invalidarClaves])

  const eliminarClave = useCallback(async (id: string): Promise<void> => {
    const token = await getAccessTokenSilently()
    await vaultFetch('vault-claves', token, { action: 'delete', id, organizacionId: orgId }, pinHeaders())
    toast.success('Clave eliminada')
    void invalidarClaves()
  }, [getAccessTokenSilently, orgId, pinHeaders, invalidarClaves])

  const abrirAfipDirecto = useCallback(async (clienteId: string): Promise<AbrirAfipDirectoResult> => {
    const pin = pinVigente(orgId, userSub)
    if (!pin) throw new Error('Vault bloqueado: ingresá el PIN primero')
    const token = await getAccessTokenSilently()
    try {
      return await afipDelegacionService.abrirAfipDirecto(token, pin, {
        clienteId,
        organizacionId: orgId,
      })
    } catch (err) {
      if ((err as { status?: number }).status === 429) bloquearVault()
      throw err
    }
  }, [getAccessTokenSilently, orgId, userSub])

  const obtenerClavesInscripcion = useCallback(async (tareaId: string): Promise<ClaveInscripcion[]> => {
    const token = await getAccessTokenSilently()
    const data = await vaultFetch('vault-claves', token, { action: 'inscripcion-ver', tareaId, organizacionId: orgId }, pinHeaders())
    return data.claves ?? []
  }, [getAccessTokenSilently, orgId, pinHeaders])

  const guardarClaveInscripcion = useCallback(async (
    tareaId: string,
    input: { organismo: string; usuario: string; clave: string },
  ): Promise<string> => {
    const token = await getAccessTokenSilently()
    const data = await vaultFetch('vault-claves', token, {
      action: 'inscripcion-guardar',
      tareaId,
      organizacionId: orgId,
      ...input,
    }, pinHeaders())
    void queryClient.invalidateQueries({ queryKey: clavesInscripcionKey(tareaId) })
    return data.id
  }, [getAccessTokenSilently, orgId, pinHeaders, queryClient])

  const organismosConClaveInscripcion = useCallback(async (tareaId: string): Promise<string[]> => {
    const token = await getAccessTokenSilently()
    const data = await vaultFetch('vault-claves', token, { action: 'inscripcion-resumen', tareaId, organizacionId: orgId })
    return data.organismos ?? []
  }, [getAccessTokenSilently, orgId])

  const transferirClavesInscripcion = useCallback(async (tareaId: string, clienteId: string): Promise<number> => {
    const token = await getAccessTokenSilently()
    const data = await vaultFetch('vault-claves', token, {
      action: 'inscripcion-transferir',
      tareaId,
      clienteId,
      organizacionId: orgId,
    })
    void invalidarClaves()
    void queryClient.invalidateQueries({ queryKey: clavesInscripcionKey(tareaId) })
    return data.pasadas ?? 0
  }, [getAccessTokenSilently, orgId, invalidarClaves, queryClient])

  return {
    hasPin,
    pinLoading,
    desbloqueado,
    tiempoRestante,
    checkPinStatus,
    verificarPin,
    configurarPin,
    cambiarPin,
    bloquear,
    obtenerClaves,
    crearClave,
    actualizarClave,
    eliminarClave,
    abrirAfipDirecto,
    obtenerClavesInscripcion,
    guardarClaveInscripcion,
    organismosConClaveInscripcion,
    transferirClavesInscripcion,
  }
}

export type { ClaveCliente, ClaveInscripcion }
